import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InsuranceDepartmentModeDto, InsuranceMode, InsuranceModesDto } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable } from '@/components/ui/layout';
import { Card, CardContent, CardHeader, CardTitle, Field } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

const MODES_KEY = ['insurance-modes'];

function modeOf(value: string): InsuranceMode | null {
  return value === 'automatic' || value === 'manual' ? value : null;
}

export function InsuranceModePanel({ embedded = false }: { embedded?: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const editable = useAuth().can('payer.manage');
  const modes = useQuery({ queryKey: MODES_KEY, queryFn: () => get<InsuranceModesDto>('/insurance/departments/modes') });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: MODES_KEY });
  const saveFacility = useMutation({
    mutationFn: (mode: InsuranceMode | null) => put<InsuranceModesDto>('/insurance/facility-mode', { mode }),
    onSuccess: (data) => queryClient.setQueryData(MODES_KEY, data),
    onError: (caught) => toast.error(tr('admin', 'could_not_save_it'), describeError(caught)),
  });
  const saveDepartment = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: InsuranceMode | null }) => put<InsuranceDepartmentModeDto>(`/insurance/departments/${id}/mode`, { mode }),
    onSuccess: () => refresh(),
    onError: (caught) => toast.error(tr('admin', 'could_not_save_it'), describeError(caught)),
  });
  const body = (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{tr('admin', 'automatic_sends_the_request_when_orders_are')}</p>
      <Field label={tr('admin', 'facility_default_for_every_payer')}>
        <ChoiceButtons
          inline
          disabled={!editable || saveFacility.isPending || modes.isLoading}
          value={modes.data?.facilityDefault ?? ''}
          onChange={(value) => {
            if (value !== (modes.data?.facilityDefault ?? '')) saveFacility.mutate(modeOf(value));
          }}
          options={[
            { value: '', label: tr('admin', 'decided_by_the_payer') },
            { value: 'automatic', label: tr('admin', 'automatic') },
            { value: 'manual', label: tr('admin', 'manual') },
          ]}
        />
      </Field>
      <div>
        <p className="mb-2 text-sm font-medium">{tr('admin', 'department_overrides')}</p>
        <DataTable
          dense
          rows={modes.data?.departments}
          loading={modes.isLoading} refreshing={modes.isFetching}
          error={modes.error}
          rowKey={(row) => row.id}
          empty={tr('admin', 'no_clinical_departments')}
          columns={[
            { header: tr('admin', 'department'), cell: (row) => row.name },
            {
              header: tr('admin', 'mode'),
              cell: (row) => (
                <ChoiceButtons
                  size="sm"
                  inline
                  aria-label={`${tr('admin', 'mode')}: ${row.name}`}
                  disabled={!editable || (saveDepartment.isPending && saveDepartment.variables?.id === row.id)}
                  value={row.insuranceMode ?? ''}
                  onChange={(value) => {
                    if (value !== (row.insuranceMode ?? '')) saveDepartment.mutate({ id: row.id, mode: modeOf(value) });
                  }}
                  options={[
                    { value: '', label: tr('admin', 'facility_default') },
                    { value: 'automatic', label: tr('admin', 'automatic') },
                    { value: 'manual', label: tr('admin', 'manual') },
                  ]}
                />
              ),
            },
          ]}
        />
    </div>
    </div>
  );
  if (embedded) return body;
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{tr('admin', 'insurance_authorisation_mode')}</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
